import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Package, SlidersHorizontal, X } from 'lucide-react';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import { useStore } from '../../hooks/useStore';
import ProductGrid from '../../components/product/ProductGrid';
import Container from '../../components/layout/Container';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Select from '../../components/common/Select';
import Breadcrumb from '../../components/common/Breadcrumb';
import EmptyState from '../../components/common/EmptyState';
import Pagination from '../../components/common/Pagination';
import CatalogFilters from '../../components/catalog/CatalogFilters';

const PAGE_SIZE = 24;

const SORT_OPTIONS = [
  { value: '', label: 'Mais relevantes' },
  { value: 'newest', label: 'Novidades' },
  { value: 'bestSelling', label: 'Mais vendidos' },
  { value: 'rating', label: 'Mais bem avaliados' },
  { value: 'price-asc', label: 'Menor preço' },
  { value: 'price-desc', label: 'Maior preço' },
];

// Lê todos os filtros relevantes da URL — fonte da verdade. Estado local
// só serve pra digitar/arrastar antes de comitar; commits voltam pra URL.
function paramsToFilters(params) {
  return {
    q: params.get('q') || '',
    categoryId: params.get('categoryId') || '',
    brand: params.get('brand') || '',
    minPrice: params.has('minPrice') ? Number(params.get('minPrice')) : null,
    maxPrice: params.has('maxPrice') ? Number(params.get('maxPrice')) : null,
    minRating: params.get('minRating') || '',
    publishedSince: params.get('publishedSince') || '',
    sort: params.get('sort') || '',
    page: Number(params.get('page') || 1),
    onSale: params.get('onSale') === 'true',
  };
}

function filtersToParams(filters) {
  const p = new URLSearchParams();
  if (filters.q) p.set('q', filters.q);
  if (filters.categoryId) p.set('categoryId', filters.categoryId);
  if (filters.brand) p.set('brand', filters.brand);
  if (filters.minPrice != null && filters.minPrice !== '') p.set('minPrice', filters.minPrice);
  if (filters.maxPrice != null && filters.maxPrice !== '') p.set('maxPrice', filters.maxPrice);
  if (filters.minRating) p.set('minRating', filters.minRating);
  if (filters.publishedSince) p.set('publishedSince', filters.publishedSince);
  if (filters.sort) p.set('sort', filters.sort);
  if (filters.page && filters.page !== 1) p.set('page', filters.page);
  if (filters.onSale) p.set('onSale', 'true');
  return p;
}

const SupplierCatalogPage = () => {
  const { supplierSlug } = useParams();
  const { store } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = paramsToFilters(searchParams);

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allCategories, setAllCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const updateFilters = useCallback((partial) => {
    const next = { ...filters, ...partial };
    // Mudou filtro → reseta página, exceto quando a partial é só de página.
    if (!('page' in partial)) next.page = 1;
    setSearchParams(filtersToParams(next));
  }, [filters, setSearchParams]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productService.getAll({
        q: filters.q || undefined,
        categoryId: filters.categoryId || undefined,
        brand: filters.brand || undefined,
        minPrice: filters.minPrice ?? undefined,
        maxPrice: filters.maxPrice ?? undefined,
        minRating: filters.minRating || undefined,
        publishedSince: filters.publishedSince || undefined,
        sort: filters.sort || undefined,
        // Ofertas: sem suporte de filtro próprio no backend ainda — usamos
        // featured como proxy (produtos em destaque tendem a ter promoção).
        featured: filters.onSale ? 'true' : undefined,
        page: filters.page,
        limit: PAGE_SIZE,
      });
      setProducts(data?.rows || []);
      setTotal(data?.count || 0);
    } catch {
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.q, filters.categoryId, filters.brand, filters.minPrice,
    filters.maxPrice, filters.minRating, filters.publishedSince,
    filters.sort, filters.onSale, filters.page,
  ]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Categorias (árvore — alimenta drill-down no aside) e marcas (distintas
  // dos produtos da loja, não da página atual) vêm de chamadas independentes
  // — filtros precisam mostrar todas as opções, não só o que está visível.
  useEffect(() => {
    let cancelled = false;
    categoryService.tree().then((data) => {
      if (cancelled) return;
      setAllCategories(Array.isArray(data) ? data : data?.rows || []);
    }).catch(() => {});

    productService.getAll({ limit: 200 }).then((data) => {
      if (cancelled) return;
      const set = new Set((data?.rows || []).map((p) => p.brand).filter(Boolean));
      setAllBrands(Array.from(set).sort());
    }).catch(() => {});

    return () => { cancelled = true; };
  }, []);

  // Range de preço dinâmico baseado nos produtos visíveis.
  const priceRange = useMemo(() => {
    const prices = products
      .flatMap((p) => (p.variations || []).map((v) => parseFloat(v.price) || 0))
      .filter((n) => n > 0);
    if (prices.length === 0) return [0, 1000];
    const max = Math.ceil(Math.max(...prices));
    return [0, max < 100 ? 100 : max];
  }, [products]);

  const clearFilters = () => setSearchParams(new URLSearchParams());

  const homePath = store?.slug && supplierSlug ? `/fornecedor/${supplierSlug}` : '/';

  // Lookup recursivo do nome da categoria selecionada na árvore. Necessário
  // porque com tree() ela pode estar aninhada em qualquer profundidade.
  const findCategoryName = (nodes, id) => {
    for (const n of nodes || []) {
      if (String(n.id) === String(id)) return n.name;
      const child = findCategoryName(n.children, id);
      if (child) return child;
    }
    return null;
  };

  return (
    <Container className="py-6">
      <Breadcrumb
        className="mb-3"
        items={[
          { label: store?.name || 'Loja', to: homePath },
          { label: 'Catálogo' },
        ]}
      />

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-app-text">
            {filters.categoryId
              ? findCategoryName(allCategories, filters.categoryId) || 'Catálogo'
              : 'Catálogo'}
          </h1>
          <p className="text-sm text-app-text-secondary mt-1">
            {loading ? 'Buscando…' : `${total} produto${total === 1 ? '' : 's'} encontrado${total === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden inline-flex items-center gap-2 px-3 py-2 border border-app-border rounded-md text-sm bg-white"
          >
            <SlidersHorizontal size={14} /> Filtros
          </button>

          <Select
            value={filters.sort}
            onChange={(e) => updateFilters({ sort: e.target.value })}
            options={SORT_OPTIONS}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <div className="hidden lg:block">
          <CatalogFilters
            filters={filters}
            onChange={updateFilters}
            categories={allCategories}
            brands={allBrands}
            priceRange={priceRange}
            onClear={clearFilters}
          />
        </div>

        <div>
          {!loading && products.length === 0 ? (
            <Card>
              <EmptyState
                icon={Package}
                title="Nenhum produto"
                description="Ajuste os filtros ou tente outra busca."
              />
            </Card>
          ) : (
            <>
              <ProductGrid products={products} loading={loading} />
              <div className="mt-6">
                <Pagination
                  page={filters.page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onPageChange={(p) => updateFilters({ page: p })}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Drawer mobile de filtros */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowMobileFilters(false)}
            aria-hidden="true"
          />
          <div className="relative ml-auto w-full max-w-sm bg-white h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 flex items-center justify-between p-3 border-b border-app-border bg-white">
              <h2 className="font-bold text-app-text">Filtros</h2>
              <button
                type="button"
                onClick={() => setShowMobileFilters(false)}
                className="p-1 rounded hover:bg-app-surface-hover"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-3">
              <CatalogFilters
                filters={filters}
                onChange={updateFilters}
                categories={allCategories}
                brands={allBrands}
                priceRange={priceRange}
                onClear={clearFilters}
              />
              <Button
                fullWidth
                className="mt-4"
                onClick={() => setShowMobileFilters(false)}
              >
                Ver resultados
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default SupplierCatalogPage;
